import { useQueryClient } from '@tanstack/react-query';
import { api, hooksApi } from '@verifime/api-definition';
import { reportPageActionToNewRelic } from '@verifime/utils';
import { useCallback } from 'react';
import { failureReason } from '../../WorkflowMetadataContext/workflowMetadataReporting';

/**
 * New Relic page actions for a dismissal that did not discard the request. The browser agent does
 * not collect console output in this deployment, so these are what engineering sees. Each carries
 * `source` (a constant label) and `status` (the HTTP status, or `network-error`): never the request
 * id, a token, the error object or anything from a response.
 */
export const DISMISS_FAILED_PAGE_ACTION = 'dismiss-request-failed';
export const DISMISS_REFUSED_PAGE_ACTION = 'dismiss-request-refused';
const SOURCE = 'dismiss-request';

/** The discard answers 409 when the request can no longer be dismissed (Closed by a grant, or its entity choice is locked). */
const CONFLICT_REASON = '409';

/**
 * What a dismissal came to.
 *
 * - `dismissed`: the request was discarded, and the open requests have been read again.
 * - `refused`: 409. The request stays; the open requests have been read again, so the list shows
 *   the server's state. The caller shows the error.
 * - `failed`: anything else, a missing or malformed request id included (the client refuses to send
 *   it). The request stays and nothing was read again. The caller shows the error.
 */
export type TDismissPermissionRequestResult = 'dismissed' | 'refused' | 'failed';

/**
 * Dismisses an open permission request (`DELETE /v1/permission/request/{id}`, the Zodios client).
 * On `dismissed` and `refused` it resolves after the open requests have been read again, so the
 * caller's list is the server's. On `failed` it reads nothing.
 * A refusal or a failure is logged and reported to New Relic by its reason alone (the status, or
 * `network-error`), never by the request or the response body.
 */
export function useDismissPermissionRequest() {
  const queryClient = useQueryClient();

  // Awaited, so the caller's list is the server's. A failed refetch does not throw (react-query's
  // default); readers retry.
  const refreshRequests = useCallback(
    () => queryClient.invalidateQueries(hooksApi.getKeyByAlias('getV1permissionrequestme')),
    [queryClient],
  );

  const dismiss = useCallback(
    async (requestId: string): Promise<TDismissPermissionRequestResult> => {
      try {
        await api.deleteV1permissionrequestId(undefined, { params: { id: requestId } });
      } catch (error) {
        const reason = failureReason(error);
        if (reason === CONFLICT_REASON) {
          reportPageActionToNewRelic(DISMISS_REFUSED_PAGE_ACTION, {
            source: SOURCE,
            status: reason,
          });
          await refreshRequests();
          return 'refused';
        }
        console.error('Dismissing the permission request failed', { status: reason });
        reportPageActionToNewRelic(DISMISS_FAILED_PAGE_ACTION, { source: SOURCE, status: reason });
        return 'failed';
      }
      await refreshRequests();
      return 'dismissed';
    },
    [refreshRequests],
  );

  return { dismiss };
}
